import { useEffect, useState, ChangeEvent } from 'react';
import { useMultiLanguage, useStarkNetSnap } from 'services';
import { useAppSelector, useAppDispatch } from 'hooks/redux';
import Toastr from 'toastr2';

import { Modal } from 'components/ui/atom/Modal';
import { setDeployModalVisible } from 'slices/modalSlice';
import { openExplorerTab, shortenAddress } from 'utils/utils';
import { Txnlink } from './DeployModal.style';
import { AccountAddressView } from 'components/ui/molecule/AccountAddress/AccountAddress.view';
import { InputWithLabel } from 'components/ui/molecule/InputWithLabel';

interface Props {
  address: string;
  addressIndex: number;
}

enum Stage {
  INIT = 0,
  WAITING_FOR_TXN = 1,
  SUCCESS = 2,
  FAIL = 3,
  WEEKLY_LIMIT_EXCEEDED = 4,
  INVITE_EXPIRED = 5,
}

export const DeployModalView = ({ address, addressIndex }: Props) => {
  const dispatch = useAppDispatch();
  const { deployAccount } = useStarkNetSnap();
  const { translate } = useMultiLanguage();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [txnHash, setTxnHash] = useState('');
  const [stage, setStage] = useState(Stage.INIT);
  const [inviteCode, setInviteCode] = useState('');
  const networks = useAppSelector((state) => state.networks);
  const chainId = networks?.items[networks.activeNetwork]?.chainId;
  const toastr = new Toastr();

  const onDeploy = async () => {
    try {
      const resp = await deployAccount(
        address,
        '0',
        chainId,
        addressIndex,
        inviteCode,
      );

      console.log('onDeploy() resp', resp);

      if (resp === false) {
        console.log('onDeploy() resp is false');
        return;
      }

      if (resp) {
        console.log('onDeploy() calling setTxnHash', resp);
        setStage(Stage.SUCCESS);
      } else {
        console.log('onDeploy() no transaction hash found???');
        throw new Error('no transaction hash');
      }
    } catch (err) {
      //eslint-disable-next-line no-console
      console.error(err);
      const msg = (err as any)?.message ?? '';
      if (msg.includes('WEEKLY_LIMIT_EXCEEDED')) {
        setStage(Stage.WEEKLY_LIMIT_EXCEEDED);
      } else if (msg.includes('INVITE_EXPIRED')) {
        setStage(Stage.INVITE_EXPIRED);
      } else {
        toastr.error(translate('deployAccountFailed'));
      }
    }
  };

  useEffect(() => {
    if (stage === Stage.SUCCESS) {
      toastr.success(translate('accountDeployedSuccessfully'));
      dispatch(setDeployModalVisible(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, dispatch]);

  const onInviteCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInviteCode(event.target.value);
  };

  const renderComponent = () => {
    switch (stage) {
      case Stage.INIT:
        return (
          <>
            <Modal.Body>
              <center>
                <AccountAddressView address={address}></AccountAddressView>
              </center>
              <InputWithLabel
                label="Invite Code"
                placeholder="Invite Code"
                onChange={onInviteCodeChange}
              />
              <br />
              <small>
                An invite code is necessary while the network is limiting the
                number of parties onboarded.{' '}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://wallet.nodefortress.io/"
                >
                  Click here
                </a>{' '}
                for more information.
              </small>
              <br />
              <br />
              <br />
              {translate('deploymentNecessaryToProceedPart1')} <br />
              <br />
              {translate('deploymentNecessaryToProceedPart2')} <br />
              <br />
              {translate('deploymentNecessaryToProceedPart3')}
            </Modal.Body>
            <Modal.Button onClick={onDeploy}>Deploy</Modal.Button>
          </>
        );
      case Stage.WAITING_FOR_TXN:
        return <Modal.Body>{translate('waitingForTransaction')}</Modal.Body>;
      case Stage.WEEKLY_LIMIT_EXCEEDED:
        return <Modal.Body>{translate('deployWeeklyLimitExceeded')}</Modal.Body>;
      case Stage.INVITE_EXPIRED:
        return <Modal.Body>{translate('deployInviteCodeExpired')}</Modal.Body>;
      case Stage.SUCCESS:
        return (
          <Modal.Body>{translate('accountDeployedSuccessfully')}</Modal.Body>
        );
      default:
        return (
          <Modal.Body>
            {translate('transactionHash')} <br />{' '}
            <Txnlink onClick={() => openExplorerTab(txnHash, 'tx', chainId)}>
              {shortenAddress(txnHash)}{' '}
            </Txnlink>
            <br />
            {translate('deployTransactionPendingPart1')} <br />
            <br />
            {translate('deployTransactionPendingPart2')} <br />
            <br />
            {translate('deployTransactionPendingPart3')}
          </Modal.Body>
        );
    }
  };

  return (
    <Modal>
      <Modal.Title>{translate('deployAccount')}</Modal.Title>
      <Modal.Logo />
      {renderComponent()}
    </Modal>
  );
};
